import type {
    AegApprovalTypeEnum,
    AegAssignToEnum,
    AegAttendanceConditionEnum,
    AegBodyBuilderNameEnum,
    AegImplementOrBodyBuilderEnum,
    AegRepairScopeEnum,
    AegRequestTypeEnum,
    AegServiceTypeEnum,
    AegStatusEnum,
    AegVehicleTypeEnum,
} from '../components/aeg/types';
import type { AegOccurrenceRequest, AegOccurrenceResponse } from '../models/newAEG';
import type { ITasOccurrenceComment } from '../models/tasOccurrence';
import ApiService from '../services/api';
import type { FileAttachment } from './tas';

const BASE_URL = import.meta.env.VITE_API_TAS;

export type AegOccurrencesFilters = {
    searchTerm?: string;
    status?: AegStatusEnum[];
    approvalType?: AegApprovalTypeEnum | '';
    vehicleType?: AegVehicleTypeEnum | '';
    serviceType?: AegServiceTypeEnum | '';
    assignTo?: AegAssignToEnum | '';
    dn?: string;
    regional?: string;
    createdBy?: string;
    chassis?: string;
};

export type AegOccurrenceSort = 'MOST_RECENT' | 'OLDEST' | 'HIGHEST_VALUE' | 'LOWEST_VALUE';

export type FetchAegOccurrencesParams = AegOccurrencesFilters & {
    page?: number;
    size?: number;
    sort?: AegOccurrenceSort;
};

const buildFilterParams = ({ status, ...filters }: AegOccurrencesFilters, params = new URLSearchParams()) => {
    status?.forEach(value => params.append('status', value));
    Object.entries(filters).forEach(([key, value]) => {
        const trimmed = value?.trim();
        if (trimmed) {
            params.set(key, trimmed);
        }
    });
    return params;
};

export type AegOccurrence = {
    id?: string | number;
    aegNumber?: string | number;
    requestNumber?: string | number;
    requestType?: AegRequestTypeEnum;
    customer?: string;
    client?: string;
    model?: string;
    licensePlate?: string;
    plate?: string;
    chassis?: string;
    approvalType?: AegApprovalTypeEnum;
    repairTotal?: number;
    responsible?: string;
    assignedTo?: string | { name?: string };
    assignTo?: string;
    status?: AegStatusEnum;
    moveTo?: AegStatusEnum;
    dn?: string;
    dealer?: string;
    createdAt?: string;
    createdBy?: string;
    date?: string;
    regional?: string;
    // cell?: string;
    vehicleType?: AegVehicleTypeEnum;
    implement?: AegImplementOrBodyBuilderEnum;
    implementOther?: string;
    bodyBuilder?: AegBodyBuilderNameEnum;
    bodyBuilderOther?: string;
    serviceType?: AegServiceTypeEnum;
    serviceCondition?: AegAttendanceConditionEnum;
    repairScope?: AegRepairScopeEnum;
    warrantyRequest?: string;
    serviceOrder?: string;
    repairDate?: string;
    stageTime?: string;
    saleDate?: string;
    odometer?: number | string;
    warrantyType?: string;
    updatedAt?: string;
    statusUpdatedAt?: string;
    phone?: string;
    dnResponsible?: string;
    occurrence?: string | number;
    osNumber?: string;
    sgNumber?: string;
    laborTotalUt?: number | string;
    laborTotal?: number | string;
    attendanceCondition?: AegAttendanceConditionEnum;
    hasThirdPartyService?: boolean;
    hasThirdParty?: boolean;
    thirdPartyServices?: string;
    thirdPartyInvoiceAmount?: number;
    thirdPartyInvoiceValue?: number;
    thirdPartyInvoiceNumbers?: string;
    thirdPartyInvoiceNumber?: string;
    partialPercentage?: string | number;
    controlCode?: string;
    chameVolksNumber?: string;
    chameVolks?: string;
    breakdown?: boolean;
    fleetComposition?: string;
    lastVwcoManPurchase?: string;
    lastPurchase?: string;
    companies?: string;
    customerComplaint?: string;
    dnDiagnosis?: string;
    dnJustification?: string;
    implementOrBodyBuilder?: AegImplementOrBodyBuilderEnum;
    implementOrBodyBuilderOther?: string;
    bodyBuilderName?: AegBodyBuilderNameEnum;
    bodyBuilderNameOther?: string;
    hourMeter?: number | string;
    cargoTransport?: string;
    vehicle?: AegOccurrenceVehicle | null;
};

export type AegOccurrenceVehicle = {
    vehicleOwner?: string;
    color?: string;
    productionDate?: string;
    billingDate?: string;
    saleDate?: string;
    tsDeliveryDate?: string;
    responsibleDealer?: string;
    warrantyEnd?: string;
    additionalWarrantyEnd?: string;
    contractNumber?: string;
    specialWarrantyEnd?: string;
    campaigns?: string;
    consultLegal?: string | boolean;
    vehicleBlocked?: boolean;
    hasWarranty?: boolean;
    hasVolksConfiaWarranty?: boolean;
    updatedAt?: string;
};

export type AegOccurrencesPage = {
    content: AegOccurrence[];
    totalElements: number;
    numberOfElements: number;
    page: number;
    size: number;
};

export type AegStatusCount = Record<AegStatusEnum, number>;

export const EMPTY_AEG_STATUS_COUNT: AegStatusCount = {
    REQUEST_OPENING: 0,
    PENDING_ANALYSIS: 0,
    PRE_APPROVAL: 0,
    WARRANTY_ANALYSIS: 0,
    CORRECT: 0,
    REJECTED: 0,
    CREDITED: 0,
};

export const fetchAegOccurrences = async (params: FetchAegOccurrencesParams = {}): Promise<AegOccurrencesPage> => {
    const { page: pageParam, size: sizeParam, sort, ...filters } = params;
    const page = pageParam ?? 0;
    const size = sizeParam ?? 10;
    const queryParams = buildFilterParams(
        filters,
        new URLSearchParams({
            page: String(page),
            size: String(size),
        })
    );
    if (sort) {
        queryParams.set('sort', sort);
    }
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<{
            content?: AegOccurrence[];
            totalElements?: number;
            numberOfElements?: number;
            page?: number;
            number?: number;
            size?: number;
        }>(`${BASE_URL}/aeg-occurrences?${queryParams.toString()}`);

        if (response.status === 200) {
            const content = response.data?.content ?? [];

            return {
                content,
                totalElements: response.data?.totalElements ?? content.length,
                numberOfElements: response.data?.numberOfElements ?? content.length,
                page: response.data?.number ?? response.data?.page ?? page,
                size: response.data?.size ?? size,
            };
        }

        throw new Error('Erro ao buscar solicitações AEG');
    } catch (error) {
        console.error('Error fetching AEG occurrences:', error);
        throw error;
    }
};

export type ExportAegOccurrencesParams = AegOccurrencesFilters & { sort?: AegOccurrenceSort };

export const exportAegOccurrencesXlsx = async ({
    sort,
    ...filters
}: ExportAegOccurrencesParams = {}): Promise<Blob> => {
    const queryParams = buildFilterParams(filters);
    if (sort) {
        queryParams.set('sort', sort);
    }
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<Blob>(`${BASE_URL}/aeg-occurrences/export?${queryParams.toString()}`, {
            responseType: 'blob',
        });

        return response.data instanceof Blob ? response.data : new Blob([response.data as BlobPart]);
    } catch (error) {
        console.error('Error exporting AEG occurrences:', error);
        throw error;
    }
};

export const fetchAegOccurrencesStatusCount = async (
    filters: Omit<AegOccurrencesFilters, 'status'> = {}
): Promise<AegStatusCount> => {
    const apiService = new ApiService(BASE_URL);
    const queryParams = buildFilterParams(filters);

    try {
        const response = await apiService.get<Partial<AegStatusCount>>(
            `${BASE_URL}/aeg-occurrences/status-count?${queryParams.toString()}`
        );

        if (response.status === 200) {
            return { ...EMPTY_AEG_STATUS_COUNT, ...response.data };
        }

        throw new Error('Erro ao buscar o status das solicitações AEG');
    } catch (error) {
        console.error('Error fetching AEG occurrence status count:', error);
        throw error;
    }
};

export type AegOccurrenceId = string | number;

export const fetchAegOccurrence = async (aegOccurrenceId: AegOccurrenceId): Promise<AegOccurrence> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<AegOccurrence>(`${BASE_URL}/aeg-occurrences/${aegOccurrenceId}`);

        if (response.status === 200 && response.data) {
            return response.data;
        }

        throw new Error('Erro ao buscar solicitação AEG');
    } catch (error) {
        console.error('Error fetching AEG occurrence:', error);
        throw error;
    }
};

export const exportAegOccurrencePdf = async (aegOccurrenceId: AegOccurrenceId): Promise<Blob> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<Blob>(`${BASE_URL}/aeg-occurrences/${aegOccurrenceId}/pdf`, {
            responseType: 'blob',
        });

        return response.data instanceof Blob
            ? response.data
            : new Blob([response.data as BlobPart], { type: 'application/pdf' });
    } catch (error) {
        console.error('Error exporting AEG occurrence PDF:', error);
        throw error;
    }
};

export type AegOccurrenceComment = Omit<ITasOccurrenceComment, 'tasOccurrenceId'> & {
    aegOccurrenceId: AegOccurrenceId;
    commentedByDescription?: string | null;
};

export type AegFile = FileAttachment & { availableToDelete?: boolean };

export type AegAuditLogEntity = 'AEG_OCCURRENCE' | 'AEG_OCCURRENCE_COMMENT' | 'AEG_OCCURRENCE_FILE';

export type AegAuditLogFieldChange = {
    input: string;
    oldData?: string | number | boolean | null;
    newData?: string | number | boolean | null;
};

export type AegAuditLog = {
    id: number;
    createdAt: string;
    action: 'CREATE' | 'UPDATE' | 'DELETE';
    entity: AegAuditLogEntity;
    entityId: number;
    userId?: string | null;
    userName?: string | null;
    description?: string | null;
    oldData?: Record<string, any> | null;
    newData?: Record<string, any> | null;
    data: AegAuditLogFieldChange[];
};

const commentsUrl = (aegOccurrenceId: AegOccurrenceId) => `${BASE_URL}/aeg-occurrences/${aegOccurrenceId}/comments`;
const filesUrl = (aegOccurrenceId: AegOccurrenceId) => `${BASE_URL}/aeg-occurrences/${aegOccurrenceId}/files`;

export const fetchAegComments = async (aegOccurrenceId: AegOccurrenceId): Promise<AegOccurrenceComment[]> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<any>(`${commentsUrl(aegOccurrenceId)}?page=0&size=100`);

        if (response.status === 200) {
            return response.data?.content ?? [];
        }

        throw new Error('Erro ao buscar chat da solicitação AEG');
    } catch (error) {
        console.error('Error fetching aeg occurrence chat:', error);
        throw error;
    }
};

export const createAegComment = async (aegOccurrenceId: AegOccurrenceId, comment: string): Promise<any> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.post<any>(commentsUrl(aegOccurrenceId), { comment });

        if (response.status === 201) {
            return response.data;
        }

        throw new Error('Erro ao criar comentário da solicitação AEG');
    } catch (error) {
        console.error('Error creating aeg occurrence comment:', error);
        throw error;
    }
};

export const updateAegComment = async (
    aegOccurrenceId: AegOccurrenceId,
    commentId: number,
    comment: string
): Promise<any> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.put<any>(`${commentsUrl(aegOccurrenceId)}/${commentId}`, { comment });

        if (response.status === 200) {
            return response.data;
        }

        throw new Error('Erro ao atualizar comentário da solicitação AEG');
    } catch (error) {
        console.error('Error updating aeg occurrence comment:', error);
        throw error;
    }
};

export const deleteAegComment = async (aegOccurrenceId: AegOccurrenceId, commentId: number): Promise<any> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.delete<any>(`${commentsUrl(aegOccurrenceId)}/${commentId}`);

        if (response.status === 204) {
            return response.data;
        }

        throw new Error('Erro ao deletar comentário da solicitação AEG');
    } catch (error) {
        console.error('Error deleting aeg occurrence comment:', error);
        throw error;
    }
};

export const fetchAegFiles = async (aegOccurrenceId: AegOccurrenceId): Promise<AegFile[]> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<any>(`${filesUrl(aegOccurrenceId)}?page=0&size=100`);

        if (response.status < 400) {
            return response.data?.content ?? [];
        }

        console.warn('Unexpected status code:', response.status);
        return [];
    } catch (error) {
        console.error('Error fetching aeg occurrence files:', error);
        return [];
    }
};

// O back recebe um arquivo por request (@RequestParam('file') MultipartFile).
export const uploadAegFile = async (aegOccurrenceId: AegOccurrenceId, file: File): Promise<boolean> => {
    const apiService = new ApiService(BASE_URL);
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await apiService.post<void>(filesUrl(aegOccurrenceId), formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });

        return response.status < 400;
    } catch (error) {
        console.error('Error uploading aeg occurrence file:', error);
        return false;
    }
};

export const downloadAegFile = async (aegOccurrenceId: AegOccurrenceId, fileId: string): Promise<Blob | null> => {
    if (!fileId) return null;

    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<Blob>(`${filesUrl(aegOccurrenceId)}/download/${fileId}`, {
            responseType: 'blob',
        });

        return response.status < 400 ? response.data : null;
    } catch (error) {
        console.error('Error downloading aeg occurrence file:', error);
        return null;
    }
};

export const removeAegFile = async (aegOccurrenceId: AegOccurrenceId, fileId: string): Promise<boolean> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.delete<void>(`${filesUrl(aegOccurrenceId)}/${fileId}`);

        return response.status < 400;
    } catch (error) {
        console.error('Error removing aeg occurrence file:', error);
        return false;
    }
};

// Traz o histórico completo (solicitação, chat e anexos); os filtros são aplicados no front porque a etapa de cada
// evento é derivada da sequência de alterações.
export const fetchAegLogs = async (aegOccurrenceId: AegOccurrenceId): Promise<AegAuditLog[]> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.get<any>(
            `${BASE_URL}/aeg-occurrences/${aegOccurrenceId}/logs?page=0&size=200`
        );

        if (response.status === 200) {
            return response.data?.content ?? [];
        }

        throw new Error('Erro ao buscar histórico da solicitação AEG');
    } catch (error) {
        console.error('Error fetching aeg occurrence logs:', error);
        throw error;
    }
};

const optionalNumber = (value?: number | string | null) => {
    if (value === null || value === undefined || value === '') {
        return undefined;
    }
    const amount = Number(value);
    return Number.isNaN(amount) ? undefined : amount;
};

const optionalText = (value?: string | number | null) => {
    if (value === null || value === undefined || String(value).trim() === '') {
        return undefined;
    }
    return String(value);
};

export const buildAegOccurrenceUpdate = (
    occurrence: AegOccurrence,
    changes: { status?: AegStatusEnum; assignTo?: string }
): AegOccurrenceRequest => {
    const status = changes.status ?? occurrence.status ?? occurrence.moveTo;

    return {
        aegNumber: optionalText(occurrence.aegNumber),
        status,
        moveTo: status,
        assignTo: changes.assignTo || occurrence.assignTo || undefined,
        requestType: occurrence.requestType,
        approvalType: occurrence.approvalType,
        responsible: optionalText(occurrence.responsible),
        dn: optionalText(occurrence.dn ?? occurrence.dealer),
        regional: optionalText(occurrence.regional),
        dnResponsible: optionalText(occurrence.dnResponsible),
        phone: optionalText(occurrence.phone),
        chassis: optionalText(occurrence.chassis),
        licensePlate: optionalText(occurrence.licensePlate ?? occurrence.plate),
        vehicleType: occurrence.vehicleType,
        customer: optionalText(occurrence.customer ?? occurrence.client),
        implementOrBodyBuilder: occurrence.implementOrBodyBuilder ?? occurrence.implement,
        implementOrBodyBuilderOther: optionalText(occurrence.implementOrBodyBuilderOther ?? occurrence.implementOther),
        bodyBuilderName: occurrence.bodyBuilderName ?? occurrence.bodyBuilder,
        bodyBuilderNameOther: optionalText(occurrence.bodyBuilderNameOther ?? occurrence.bodyBuilderOther),
        cargoTransport: optionalText(occurrence.cargoTransport),
        odometer: optionalNumber(occurrence.odometer),
        hourMeter: optionalNumber(occurrence.hourMeter),
        repairDate: optionalText(occurrence.repairDate),
        osNumber: optionalText(occurrence.osNumber ?? occurrence.serviceOrder),
        sgNumber: optionalText(occurrence.sgNumber ?? occurrence.warrantyRequest),
        serviceType: occurrence.serviceType,
        repairTotal: occurrence.repairTotal,
        laborTotalUt: optionalNumber(occurrence.laborTotalUt ?? occurrence.laborTotal),
        hasThirdPartyService: occurrence.hasThirdPartyService ?? occurrence.hasThirdParty,
        thirdPartyServices: optionalText(occurrence.thirdPartyServices),
        thirdPartyInvoiceAmount: occurrence.thirdPartyInvoiceAmount ?? occurrence.thirdPartyInvoiceValue,
        thirdPartyInvoiceNumbers: optionalText(
            occurrence.thirdPartyInvoiceNumbers ?? occurrence.thirdPartyInvoiceNumber
        ),
        repairScope: occurrence.repairScope,
        partialPercentage: optionalNumber(occurrence.partialPercentage),
        controlCode: optionalText(occurrence.controlCode),
        chameVolksNumber: optionalText(occurrence.chameVolksNumber ?? occurrence.chameVolks),
        breakdown: occurrence.breakdown === undefined ? undefined : occurrence.breakdown ? 'YES' : 'NO',
        customerComplaint: optionalText(occurrence.customerComplaint),
        dnDiagnosis: optionalText(occurrence.dnDiagnosis),
        dnJustification: optionalText(occurrence.dnJustification),
        attendanceCondition: occurrence.attendanceCondition ?? occurrence.serviceCondition,
        fleetComposition: optionalText(occurrence.fleetComposition),
        lastVwcoManPurchase: optionalText(occurrence.lastVwcoManPurchase ?? occurrence.lastPurchase),
        companies: optionalText(occurrence.companies),
    };
};

export const updateAegOccurrence = async (
    aegOccurrenceId: AegOccurrenceId,
    payload: AegOccurrenceRequest
): Promise<AegOccurrenceResponse> => {
    const apiService = new ApiService(BASE_URL);
    const response = await apiService.put<AegOccurrenceResponse>(
        `${BASE_URL}/aeg-occurrences/${aegOccurrenceId}`,
        payload
    );

    if (response.status >= 200 && response.status < 300) {
        return response.data;
    }

    throw new Error('Erro ao atualizar solicitação AEG');
};

export const createAegOccurrence = async (payload: AegOccurrenceRequest): Promise<AegOccurrenceResponse | null> => {
    const apiService = new ApiService(BASE_URL);

    try {
        const response = await apiService.post<AegOccurrenceResponse>(`${BASE_URL}/aeg-occurrences`, payload);

        return response.data;
    } catch (error) {
        console.error('Error creating AEG occurrence:', error);
        return null;
    }
};
